import { zodResolver } from "@hookform/resolvers/zod"
import { ReactNode, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import ReactQuill from "react-quill"
import { useCreateEmailMutation } from "../../../../features/mailsApi"
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice"
import { useAppDispatch } from "../../../lib/redux/hooks"
import Button from "../../../ui/molecules/button"
import Tiptap from "../../../ui/molecules/editorInput/tiptap"
import Input from "../../../ui/molecules/input"
import { ProjectDTO } from "../dto"
import { translateVariables, variablesMap } from "./utils"
import { TemplateFormValues, templateSchema } from "./validation"
import ReactDOM from "react-dom"
import EditorInput from "../../../ui/molecules/editorInput/index copy"

interface Props {
    project: ProjectDTO
    updateTemplate: (html: string) => string[]
    stripHtml: (html: string) => string
}

export function CreateTemplate({ project, updateTemplate, stripHtml }: Props) {
    const dispatch = useAppDispatch()
    const menuRef = useRef<HTMLTableCellElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<ReactQuill>(null)
    const [openVariables, setOpenVariables] = useState(false)

    const createForm = useForm<TemplateFormValues>({
        resolver: zodResolver(templateSchema),
    })

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setOpenVariables(false);
        }
    };

    useEffect(() => {
        if (openVariables) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openVariables]);

    let variableOptions: ReactNode[] = []
    variablesMap.forEach((key, item) => {
        variableOptions.push(
            <Button
                onClick={() => {
                    if (quillRef.current) {
                        const editor = quillRef.current.getEditor();
                        const range = editor.getSelection();

                        if (range) {
                            editor.insertText(range.index, "[" + item + "]");

                            createForm.setValue('templateText', editor.getText());
                            createForm.setValue('templateHTML', editor.root.innerHTML);
                        }
                    }
                }}
                label={item}
                size={'sm'}
                iconPosition={'left'}
                variant={'link'}
                color={'gray'}
            />
        );
    });

    useEffect(() => {
        if (project && project.emailFrom && project.nameFrom) {
            createForm.reset({
                fromEmail: project.emailFrom,
                fromEmailName: project.nameFrom,
                projectId: project.id,
            })
        }
    }, [project])

    const [createCustomMail] = useCreateEmailMutation()
    const handleCreateCustomMail = async () => {
        try {
            await createCustomMail({
                ...createForm.getValues(),
            }).unwrap()
            createForm.setValue('templateName', '')
            createForm.setValue('subject', '')
            createForm.setValue('templateText', '')
            createForm.setValue('templateHTML', '')
            dispatch(addBannerWithTimeout({ message: "Email personalizzata creata con successo", variant: "success" }))
        } catch (error) {
            let message = "Errore durante la creazione dell'email personalizzata"
            const _error = error as { status: number, data: string }
            if (_error.data === 'subject already exists') {
                message = "Esiste già un template con questo oggetto"
            }
            dispatch(addBannerWithTimeout({ message, variant: "error" }))
        }
    }

    return (
        <div className="flex flex-col w-full">
            <div className='flex flex-col w-full px-4 gap-4'>
                <div className='flex flex-col gap-3'>
                    <Input
                        label='Nome template'
                        value={createForm.watch('templateName')}
                        onChange={(e) => { createForm.setValue('templateName', e.target.value) }}
                        error={!!createForm.formState.errors.templateName}
                        supportingText={createForm.formState.errors.templateName?.message as string | undefined}
                        placeholder="Specificare il nome del template"
                    />
                </div>
                <div className='flex flex-col gap-3'>
                    <Input
                        label='Oggetto'
                        value={createForm.watch('subject')}
                        onChange={(e) => { createForm.setValue('subject', e.target.value) }}
                        placeholder="Specificare l'oggetto della mail"
                        error={!!createForm.formState.errors.subject}
                        supportingText={createForm.formState.errors.subject?.message as string | undefined}
                    />
                </div>
                <div className='flex flex-col gap-11'>
                    {/* <EditorInput
                        id='input-editor-settings-email-save'
                        key='input-editor-settings-email-save'
                        label='Contenuto della mail'
                        onChangeHtml={(e) => createForm.setValue('templateHTML', e)}
                        html={translateVariables(createForm.watch('templateHTML'))}
                        // onChangeText={(e) => {
                        //     const parser = new DOMParser()
                        //     const doc = parser.parseFromString(e, "text/html")

                        //     const links = doc.querySelectorAll("a")

                        //     if (links.length > 0) {
                        //         links.forEach((link) => {
                        //             let href = link.getAttribute("href")

                        //             if (href && !href.startsWith("http://") && !href.startsWith("https://")) {
                        //                 link.setAttribute("href", `https://${href}`)
                        //             }
                        //         })

                        //         setCreateTemplateText(doc.body.innerHTML)
                        //     } else {
                        //         setCreateTemplateText(e)
                        //     }
                        // } }
                        onChangeText={e => createForm.setValue('templateText', e)}
                        placeholder="Descrivere il contenuto dell'e-mail"
                        ref={quillRef}
                    /> */}
                    <Tiptap
                        label="Contenuto della mail"
                        onChangeHtml={(e) => createForm.setValue('templateHTML', e)}
                        onChangeText={e => createForm.setValue('templateText', e)}
                        content={translateVariables(createForm.watch('templateHTML'))}
                        variables={Array.from(variablesMap.entries()).map(([key, value]) => ({ label: key, value }))}
                    />
                    {/* <div ref={buttonRef} className='relative flex flex-col items-end justify-center'>
                        <Button onClick={() => setOpenVariables(prev => !prev)} label="Inserisci una variabile" size={'sm'} iconPosition={'left'} variant={'link'} color={'blue'} />
                        {ReactDOM.createPortal((
                            openVariables ?
                                <div ref={menuRef} className="bg-white border-[1px] border-neutral-100 rounded-md absolute shadow-sm z-[100]"
                                    style={{
                                        top: buttonRef.current ? (buttonRef.current.getBoundingClientRect().top + 40) : 0,
                                        right: buttonRef.current ? buttonRef.current.getBoundingClientRect().left : 0,
                                    }}
                                >
                                    {variableOptions}
                                </div> : null
                        ), document.body)}
                    </div> */}
                </div>
            </div>
            <div className=' flex justify-end items-center w-full border-t-0 gap-2 pt-4 px-2 border-neutral-200'>
                <Button
                    onClick={() => {
                        createForm.handleSubmit(handleCreateCustomMail)()
                    }}
                    disabled={!project.emailFrom || !project.nameFrom}
                    label={'Crea template'}
                    size={'sm'}
                    iconPosition={'left'}
                    variant={'solid'}
                    color={'blue'}
                />
            </div>
        </div>
    )
}